import React from "react";
import { Button } from "../../ui";
import { StatusChip } from "./statusMeta";
import { useGetMyContributionsQuery, type UserContribution } from "../../services/rtkApi/contributeApi";

// The member's own list of pitched ideas, newest first. Shows status, the
// confirmed summary, and — once an idea ships — the props they earned and a link to
// the live feature. Drafts can be resumed back in the chat.

function fmtDate(iso?: string): string {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
  } catch {
    return "";
  }
}

function Item({ c, onResume }: { c: UserContribution; onResume?: () => void }) {
  const shipped = c.status === "shipped";
  return (
    <div className="border-brutal border-ink rounded-sm bg-paper shadow-brutal-sm p-3 space-y-2">
      <div className="flex items-start justify-between gap-2">
        <div className="font-bold text-sm break-words">{c.title || "(untitled idea)"}</div>
        <StatusChip status={c.status} />
      </div>
      {c.pitch && <div className="text-sm text-ink/70 whitespace-pre-wrap break-words">{c.pitch}</div>}

      <div className="flex items-center flex-wrap gap-2 text-[11px] text-ink/50">
        <span>{fmtDate(c.submittedAt || c.createdAt)}</span>
        {c.voiceUsed && <span>· 🎙 voiced</span>}
      </div>

      {shipped && (
        <div className="border-2 border-ink rounded-sm bg-mint/20 p-2 text-xs space-y-1">
          <div className="font-bold">✨ Built from your idea — thank you!</div>
          {(c.credit?.respect ?? 0) > 0 && <div>+{c.credit.respect} respect{c.credit.badgeGranted ? " · Contributor badge" : ""}</div>}
          {c.shipped?.featureUrl && (
            <a href={c.shipped.featureUrl} className="underline text-sky-700 break-all" target="_blank" rel="noopener noreferrer">
              {c.shipped.featureTitle || "see the feature"}
            </a>
          )}
        </div>
      )}

      {c.status === "draft" && onResume && (
        <Button size="sm" variant="outline" onClick={onResume}>
          continue this draft
        </Button>
      )}
    </div>
  );
}

export default function MyContributions({ onResume }: { onResume?: () => void }) {
  const { data, isLoading, isError } = useGetMyContributionsQuery();
  const list = data?.contributions || [];

  if (isLoading) return <div className="text-sm text-ink/50 p-4">loading your ideas…</div>;
  if (isError) return <div className="text-sm text-red-600 p-4">couldn't load your ideas — try again.</div>;
  if (list.length === 0) {
    return (
      <div className="border-2 border-dashed border-ink/40 rounded-sm p-6 text-center text-sm text-ink/60">
        No ideas yet — pitch your first one at the Idea Desk! If it gets built, it shows up here as your contribution.
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {list.map((c) => (
        <Item key={c.id} c={c} onResume={c.status === "draft" ? onResume : undefined} />
      ))}
    </div>
  );
}
